/**
 * TopVendorsSection Component
 * Collapsible top vendors by spend within Cash & P&L tab.
 * Sortable by vendor name, spend, or invoice count.
 */

import { useState, useMemo } from 'react';
import { SectionCard } from './SectionCard';
import { formatCurrency } from '@/utils/formatters';
import type { ExpenseSummaryResponse } from '@/api/cashApi';

interface TopVendorsSectionProps {
  topVendors: ExpenseSummaryResponse['data']['topVendors'];
}

type SortField = 'name' | 'spend' | 'count';
type SortDir = 'asc' | 'desc';

export function TopVendorsSection({ topVendors }: TopVendorsSectionProps) {
  const [sortField, setSortField] = useState<SortField>('spend');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  const sorted = useMemo(() => {
    return [...topVendors].sort((a, b) => {
      let cmp: number;
      if (sortField === 'spend') cmp = a.totalSpend - b.totalSpend;
      else if (sortField === 'count') cmp = a.invoiceCount - b.invoiceCount;
      else cmp = a.name.localeCompare(b.name);
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [topVendors, sortField, sortDir]);

  const toggleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir((d) => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir(field === 'name' ? 'asc' : 'desc');
    }
  };

  const arrow = (field: SortField) => {
    if (sortField !== field) return '\u25B4';
    return sortDir === 'asc' ? '\u25B2' : '\u25BC';
  };

  return (
    <SectionCard title="Top Vendors by Spend">
      {sorted.length > 0 ? (
        <div className="cash-pnl-dashboard__table">
          <div className="cash-pnl-dashboard__table-header" style={{ cursor: 'pointer' }}>
            <span onClick={() => toggleSort('name')}>
              Vendor {arrow('name')}
            </span>
            <span onClick={() => toggleSort('spend')} style={{ textAlign: 'right' }}>
              Total Spend {arrow('spend')}
            </span>
          </div>
          {sorted.map((vendor) => (
            <div key={vendor.contactId} className="cash-pnl-dashboard__table-row">
              <span className="cash-pnl-dashboard__account-name">
                {vendor.name}
                <span className="cash-pnl-dashboard__percentage">
                  {vendor.invoiceCount} bills
                </span>
              </span>
              <span className="cash-pnl-dashboard__balance">
                {formatCurrency(vendor.totalSpend)}
              </span>
            </div>
          ))}
        </div>
      ) : (
        <p className="cash-pnl-dashboard__empty">No vendor data available</p>
      )}
    </SectionCard>
  );
}
